// 3rd-party modules
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";


// project modules
import AccountKiboshDeviceModal from "../../components/accounts/kibosh-device/AccountKiboshDeviceModal";
import AccountDeviceExplicitLogs from "../../components/accounts/device-explicit-logs/AccountDeviceExplicitLogs";
import AccountDeviceMalwareLogs from "../../components/accounts/device-malware-logs/AccountDeviceMalwareLogs";

import Loader from "../../components/shared/loader";
import { apiCall } from "../../helpers/apiHelper";
import { RootState } from "../../stores";
import { CONSTANTS } from "../../helpers/defines";
import InfoPopup from "../../components/shared/popup/infoPopup";
import Popup from "../../components/shared/popup/popup";



// apis
import * as AuthApi from "../../apis/authApi"
import * as DashApi from "../../apis/dashApi";

// modals
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
import { AccountKiboshDeviceStatus } from "../../models/accountKiboshDeviceStatus";
import { AccountDevice, AccountDeviceBandwidthLog } from "../../models/accountDevice";
import { KiboshDeviceWifiConfig } from "../../models/kiboshDeviceWifiConfig";
import { AccountBlockedSite } from "../../models/accountBlockedSite";
import { isMobile, isIOS } from "react-device-detect";
import { Account } from "../../models/account";
import AccountLatencyLogs from '../../components/accounts/latency-logs/AccountLatencyLogs';

import HandPointingRightSvg from '../../assets/icons/framework7io-mit-license-hand-pointing-right.svg';


export default function HomePage() {
  const kiboshDevice = useSelector((state: RootState) => state.user?.kiboshDevice) || new AccountKiboshDevice();

  const [currentAccount, setCurrentAccount] = useState<Account>(new Account());
  const [currentKiboshDeviceStatus, setCurrentKiboshDeviceStatus] = useState<AccountKiboshDeviceStatus>(new AccountKiboshDeviceStatus());
  const [connectedDevicesCount, setConnectedDevicesCount] = useState<number>(0);
  const [vpnsCount, setVpnsCount] = useState<number>(0);
  const [malwaresCount, setMalwaresCount] = useState<number>(0);
  const [explicitCount, setExplicitCount] = useState<number>(0);
  const [downloadSpeed, setDownloadSpeed] = useState<number>(0);
  const [uploadSpeed, setUploadSpeed] = useState<number>(0);
  const [downloadSpeedUnit, setDownloadSpeedUnit] = useState<string>("MB");
  const [uploadSpeedUnit, setUploadSpeedUnit] = useState<string>("MB");
  const [loading, setLoading] = useState<boolean>(false);
  const [preSelectedTab, setPreSelectedTab] = useState<string>("");
  const [malwareLastUpdate, setMalwareLastUpdate] = useState<string>("");
  const [explicitLastUpdate, setExplicitLastUpdate] = useState<string>("");
  const [showAccountKiboshDeviceModal, setShowAccountKiboshDeviceModal] = useState(false);
  const [showLatencyModal, setShowLatencyModal] = useState(false) 
  const [showExplicitModal, setShowExplicitModal] = useState(false)
  const [showMalwareModal, setShowMalwareModal] = useState(false)
  const [malwareDevicesOnly, setMalwareDevicesOnly] = useState(false);
  const [explicitDevicesOnly, setExplicitDevicesOnly] = useState(false);

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [showHomescreenInstallButton, setShowHomescreenInstallButton] = useState<boolean>(false)
  const [iosInstructionPopupVisible, setIosInstructionPopupVisible] = useState<boolean>(false)

  const [kiboshDeviceWifiConfig, setKiboshDeviceWifiConfig] = useState<any>(null)
  const [globalBlockedSites, setGlobalBlockedSites] = useState<any>(null)

  const [preferredTimezone, setPreferredTimezone] = useState<string>("");
  const [currentTime, setCurrentTime] = useState<string>("");

  const [initialized, setInitialized] = useState<boolean>(false)


  let abortController = new AbortController();



  useEffect(() => {
    let abortController = new AbortController();

    const handler = (e:any) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setShowHomescreenInstallButton(true)
    }
    window.addEventListener('beforeinstallprompt', handler)


    return () => { abortController.abort(); }
  }, []);


  useEffect(() => {
    getDataAsync();
    getPreferredTimezoneAsync();
  }, [kiboshDevice]);



  useEffect(() => {
    if (preferredTimezone) {
      const interval = setInterval(() => {
        setCurrentTime(moment().tz(preferredTimezone).format("hh:mm A"));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [preferredTimezone]);



  const getPreferredTimezoneAsync = async () => {
    setPreferredTimezone(await getPreferredTimezone(abortController.signal));
  }

  const getPreferredTimezone = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getPreferredTimezone(kiboshDevice.kiboshDeviceReference!, abortSignal));

    return response.success ? response.data?.value || [] : [];
  };


  const handleInstallClick = async () => {
    if(isMobile && !isIOS) {
    
      if (!deferredPrompt) { return }

      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      console.log(`User response to the install prompt: ${outcome}`)

      if (outcome === 'accepted') {
        console.log('PWA installed successfully')
      }

      setDeferredPrompt(null)
      setShowHomescreenInstallButton(false)
    }
    else {
      setIosInstructionPopupVisible(true);
    }
  }


  const getDataAsync = async () => {
    if (!kiboshDevice.kiboshDeviceReference) {
      setInitialized(true);
      return;
    }
    setLoading(true);
    Promise.all([
      getAccountKiboshDeviceStatus(abortController.signal),
      getAccountDevices(abortController.signal),
      getDeviceLatestBandwidths(abortController.signal),
      getAccount(abortController.signal),
      getDomainListLastUpdate(abortController.signal),
      getKiboshDeviceWifiConfig(abortController.signal),
      getGlobalBlockedSites(abortController.signal)
    ]).then(
      (results: any) => {
        const kiboshDeviceStatus = results[0];
        const devices = results[1];
        const bandwidth = results[2];

        if (results[3]) {
          setCurrentAccount(results[3]);
        }


	//alert(JSON.stringify(kiboshDeviceStatus))

        const domainListLastUpdateData = results[4];

        if(results[5]) {
          setKiboshDeviceWifiConfig(results[5]);
        }
        if(results[6]) {
          setGlobalBlockedSites(results[6]);
        }


        if (devices && devices.length > 0) {
          //setConnectedDevicesCount(devices.filter((x: AccountDevice) => x.pingMs && x.pingMs > 0).length);
          setConnectedDevicesCount(devices.filter((x: AccountDevice) => x.connectionStatus != "offline" ).length);
          setVpnsCount(devices.filter((x: AccountDevice) => x.isVpn).length);
          setMalwaresCount(devices.map((x: AccountDevice) => x.detectedMalware ? x.detectedMalware.filter(malware => moment().add(-24, "hours") < moment(malware.lastQueried)).length : 0).reduce((a: any, b: any) => a + b));
          setExplicitCount(devices.map((x: AccountDevice) => x.detectedExplicit ? x.detectedExplicit.filter(explicit => moment().add(-24, "hours") < moment(explicit.lastQueried)).length : 0).reduce((a: any, b: any) => a + b));
        }
        if(kiboshDeviceStatus.kiboshDevice.malwareCount != null ) {
          //alert(JSON.stringify(kiboshDeviceStatus))
          setMalwaresCount(kiboshDeviceStatus.kiboshDevice.malwareCount)
          setExplicitCount(kiboshDeviceStatus.kiboshDevice.explicitCount)
        }

        setCurrentKiboshDeviceStatus(kiboshDeviceStatus);

        const downloadSpeedObj = getNormalizedSpeedWithUnit(bandwidth.length ? bandwidth[0].intervalDownloadBandwidth : 0);
        const uploadSpeedObj = getNormalizedSpeedWithUnit(bandwidth.length ? bandwidth[0].intervalUploadBandwidth : 0);

        setDownloadSpeed(downloadSpeedObj.speed);
        setDownloadSpeedUnit(downloadSpeedObj.unit);
        setUploadSpeed(uploadSpeedObj.speed);
        setUploadSpeedUnit(uploadSpeedObj.unit);

        setMalwareLastUpdate(domainListLastUpdateData ? moment(domainListLastUpdateData.malware).format(CONSTANTS.DEFAULT_DATETIME_FORMAT)  : "")
        setExplicitLastUpdate(domainListLastUpdateData ? moment(domainListLastUpdateData.explicit).format(CONSTANTS.DEFAULT_DATETIME_FORMAT)  : "")
      }
    ).finally(() => {
      setLoading(false);
      setInitialized(true);
    });
  }

  const getGlobalBlockedSites = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getClientDeviceFilters(kiboshDevice?.kiboshDeviceReference!, "lan", abortSignal));

    return response.success ? AccountBlockedSite.toArrayOfClass(response.data?.value || []) : [];
  };

  const getKiboshDeviceWifiConfig = async (abortSignal?: AbortSignal) => {
    setLoading(true);
    const response = await apiCall(DashApi.getKiboshDeviceWifiConfig(kiboshDevice?.kiboshDeviceReference!, abortSignal));
    setLoading(false);

    return response.success && response.data?.value ? KiboshDeviceWifiConfig.toClass(response.data?.value) : null;
  };



  const getAccount = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(AuthApi.getAccount(abortSignal));
    return response.success ? Account.toClass(response.data?.value) : null;
  };

  const getAccountDevices = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getClientDevices(kiboshDevice?.kiboshDeviceReference!, abortSignal));
    return response.success ? AccountDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getAccountKiboshDeviceStatus = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getKiboshDeviceStatus(kiboshDevice?.kiboshDeviceReference!, abortSignal));
    return response.success ? AccountKiboshDeviceStatus.toClass(response.data?.value) : null;
  };

  const getDeviceLatestBandwidths = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getDeviceLatestBandwidths(kiboshDevice?.kiboshDeviceReference!, true, abortSignal));

    return response.success ? AccountDeviceBandwidthLog.toArrayOfClass(response.data?.value || []) : [];
  };


  const getDomainListLastUpdate = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getDomainListLastUpdate(kiboshDevice?.kiboshDeviceReference!, abortSignal));
    return response.success ? response.data?.value : null;
  };

  const getInternetLatencyClass = (): string => {
    const ping = currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs;
    if (!ping) {
      return "No Connection"
    }
    else if (ping < 41) {
      return "Excellent"
    }
    else if (ping < 81) {
      return "Good"
    }
    else if (ping < 151) {
      return "OK"
    } else {
      return "Poor"
    }
  }

  const getInternetLatencyClassColor = (): string => {
    const internetLatencyClass = getInternetLatencyClass();
    switch (internetLatencyClass) {
      case "Excellent":
        return "blue";
      case "Good":
        return "green";
      case "OK":
        return "orange";
      default:
        return "red";
    }
  }

  const getNormalizedSpeedWithUnit = (data: any): any => {
    let unit = "B";
    let speed = data;

    let unitClass = 1;
    while (speed / 1024 >= 1) {
      speed /= 1024;
      unitClass += 1;
    }

    switch (unitClass) {
      case 1:
        unit = "B";
        break;
      case 2:
        unit = "KB";
        break;
      case 3:
        unit = "MB";
        break;
      case 4:
        unit = "GB";
        break;
      case 5:
        unit = "TB";
        break;
      case 6:
        unit = "PB";
        break;
    }

    return { unit, speed }
  }

  const onMalwareClick = () => {
    if (malwaresCount && kiboshDevice.kiboshDeviceTypeCode != "dns" ) {
      setMalwareDevicesOnly(true);
      setExplicitDevicesOnly(false);
      setPreSelectedTab("devices");
      setShowAccountKiboshDeviceModal(true);
    } else if(malwaresCount && kiboshDevice.kiboshDeviceTypeCode == "dns" ) {
      setShowMalwareModal(true)
    }

  }
  const onExplicitClick = () => {
    if (explicitCount && kiboshDevice.kiboshDeviceTypeCode != "dns" ) {
      setExplicitDevicesOnly(true);
      setMalwareDevicesOnly(false);
      setPreSelectedTab("devices");
      setShowAccountKiboshDeviceModal(true);
    } else if(explicitCount && kiboshDevice.kiboshDeviceTypeCode == "dns" ) {
      setShowExplicitModal(true)
    }
  }
  const onSelectedTab = (selectedTab: any) => {
    if (selectedTab) {
      setMalwareDevicesOnly(false);
      setExplicitDevicesOnly(false);
      setPreSelectedTab(selectedTab);
      setShowAccountKiboshDeviceModal(true);
    }
  };




  const onFirmwareClick = () => {
    setMalwareDevicesOnly(false);
    setExplicitDevicesOnly(false);
    setPreSelectedTab("settings-firmware-upgrade");
    setShowAccountKiboshDeviceModal(true);
  }

  const getWifiString = () => {
    let wsParts = []
    let testVars = [ ["radio24GhzEnabled", "radio24GhzName"], ["radio5GhzEnabled", "radio5GhzName"], ["radioGuest24GhzEnabled", "radioGuest24GhzName"], ["radioGuest5GhzEnabled", "radioGuest5GhzName"] ]
    if(kiboshDeviceWifiConfig != null) {
      for(let testIndex=0; testIndex < testVars.length; testIndex++) {
        if( kiboshDeviceWifiConfig[ testVars[testIndex][0] ]) {
          wsParts.push(  kiboshDeviceWifiConfig[ testVars[testIndex][1] ] )
        }
      }
    }
    return "" + wsParts.join(" | ")
  }



  const getInternetStatus = ():any => {
    let ckds =  currentKiboshDeviceStatus
    let kd =    kiboshDevice

    let kdcs  = ckds?.kiboshDevice?.connectionStatus;
    let kdrds = ckds?.kiboshDevice?.remoteDnsStatus;
    let kdt =   kd.kiboshDeviceTypeCode;

    if(kdcs == null || kdcs == "" || kdt == null || kdt == "" ) {
      return <text data-weight="600" data-wrap="wrap">Internet:<text data-color="red">{ initialized ? "N/A - No Device Associated With Account" : "" }</text></text> 
    }
    else if(kdt == "router") {
      let istatus = kdcs == "connected" && kdrds == "connected" ? "Protected" : ( kdcs == "connected" ? "Online (raw)" : "Offline" )
      let color   = kdcs == "connected" && kdrds == "connected" ? "main-dark" : ( kdcs == "connected" ? "orangered"    : "red" )
      if (color == "orangered" ) { 
        return <text data-weight="600" data-wrap="wrap">Internet:<a href="https://kibosh.com/docs/online-raw"><text data-cursor="pointer" data-color={ color } >{ istatus }</text></a></text>

      } else {
        return <text data-weight="600" data-wrap="wrap">Internet:<text data-color={ color } >{ istatus }</text></text>
      }
    }
    else if(kdt == "dns") {
      return <text data-weight="600" data-wrap="wrap">Your KiboshDNS is {kdcs === "disconnected" ? <text data-secondary data-cursor="pointer" onClick={() => onSelectedTab("agents")}>Unknown</text> : ckds?.kiboshDevice?.wanPingMs ? <text data-color="main-dark">online</text> : <text data-color="red" data-cursor="pointer" onClick={() => onSelectedTab("agents")}>offline</text>}</text>
    }
    else { //kdt == vpn
      return <text data-weight="600" data-wrap="wrap">Your KiboshVPN is {ckds?.kiboshDevice?.wanPingMs ? <text data-color="main-dark">online</text> : <text data-color="red">offline</text>}</text>
    }

  }

  const firstLetterToUpperCase = (str:any):string => {
    return String(str).charAt(0).toUpperCase() + String(str).slice(1);
  }

  const getSubscriptionStatus = ():any => {

    let ckds =  currentKiboshDeviceStatus
    let kd =    kiboshDevice

    let kdcs  = ckds?.kiboshDevice?.connectionStatus;
    let kdss =  ckds?.kiboshDevice?.subscriptionStatus
    let kdt =   kd.kiboshDeviceTypeCode;

    let color = "main-dark"
    let sstatus = ""
    let statusPrefix = kdt === "router" ? "Pro" : ( kdt === "dns" ? "Lite" : ( kdt === "vpn" ? "Mobile" : "Subscription" ))

    if(kdcs == null || kdcs == "" || kdt == null || kdt == "" ) {
      color = "red"
      sstatus = initialized ? "Click Here To Activate" : ""
    } else {
      color = kdss === "active" ? "main-dark" : "red"
      sstatus = kdss || "no subscription"
    }

 

    return <text data-wrap="wrap" data-weight="600">{ statusPrefix } Status:<text data-weight="700" data-wrap="wrap" data-color={color} >{ firstLetterToUpperCase(sstatus) }</text></text>
    
  }

  const getPortalCheckin = ():any => {
    let ldc:any = currentKiboshDeviceStatus?.kiboshDevice?.lastDeviceCheckin
    
    let ldcStr:string = "-"
    let old = false;
    if(ldc != null) {
      let ldcm = moment(ldc)
      old = moment().subtract(1, 'days').isAfter(ldcm)
      ldcStr = old ? ldcm.format(CONSTANTS.DEFAULT_DATETIME_FORMAT) : ldcm.format(CONSTANTS.DEFAULT_TIME_FORMAT) 
    }
    
    return <text data-weight="700" data-wrap="wrap" data-color="main-dark">{ ldcStr }</text>
  } 

  return (
    <>
      {loading && <Loader />}
      <view data-vertical="">
        <view
          data-scroll=""
          data-space="20">
          { 
            kiboshDevice.kiboshDeviceTypeCode === "router" && 
            currentKiboshDeviceStatus?.kiboshDevice?.currentFirmwareVersion &&
            currentKiboshDeviceStatus?.kiboshDevice?.currentFirmwareVersion != "" && 
            currentKiboshDeviceStatus?.latestFirmwareVersion != currentKiboshDeviceStatus?.kiboshDevice?.currentFirmwareVersion &&
              <group data-space="10" data-wrap="no" style={{ backgroundColor: "#F00", color: "#FFF"  }} data-align="center">
                <text data-weight="700" data-text-size="x-large" data-wrap="wrap" >Your Firmware is out of date. <a style={{ textDecoration: "underline" }} href="/portal/dash?tab=settings-firmware-upgrade">Please Upgrade Now</a></text>
              </group>
          }
          <group data-gap="10" data-direction="column">
            <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">Portal</text>
          </group>
          <section data-space="1" data-height={ isMobile ? 'autofit' : undefined} data-margin-bottom={ isMobile ? "80" : undefined}>
            <group data-gap="5" data-height={ isMobile ? 'fit' : undefined}>
              <group>
                <space data-height="5"></space>
                <group data-gap="10" data-direction="column">
                  {
                    getInternetStatus()
                  }
                  <text data-weight="600" data-wrap="wrap">
                    {  kiboshDevice.kiboshDeviceTypeCode == "dns" ? "Agent Update:" : "Portal Update:" }
                    { getPortalCheckin() }
                  </text>
                  <text data-wrap="wrap" data-weight="600">
                    WAN IP:
                    <text data-weight="700" data-wrap="wrap" data-color="main-dark">
                      {currentKiboshDeviceStatus?.kiboshDevice?.currentExternalIp || "-"}
                    </text>
                  </text>
                  { kiboshDevice.kiboshDeviceTypeCode === "router" &&
                    <a href="/portal/dash?tab=settings" target="_blank" >
                      <text data-wrap="wrap" data-weight="600">
                        WiFi:
                        <text data-weight="700" data-wrap="wrap" data-color="main-dark">
                          { getWifiString() }
                        </text>
                      </text>
                    </a>
                  }

                  <a href={ "/portal/account-details?tab=" + ( kiboshDevice.kiboshDeviceReference ? "subscriptions" : "activation" ) } target="_blank" >
                    { getSubscriptionStatus() }
                  </a>
                  {
                    kiboshDevice.kiboshDeviceTypeCode === "dns" &&
                    <text data-wrap="wrap" data-weight="600" >
                      DNS ID:
                      <text data-weight="700" data-wrap="wrap" data-color="main-dark">
                        {currentKiboshDeviceStatus?.kiboshDevice?.id || "-"}
                      </text>
                    </text>
                  }
                  {
                    kiboshDevice.kiboshDeviceTypeCode === "router" &&
                    <text data-wrap="wrap" data-weight="600" data-cursor="pointer" onClick={onFirmwareClick}>
                      Firmware:
                      <text data-weight="700" data-wrap="wrap" data-color={currentKiboshDeviceStatus?.latestFirmwareVersion === currentKiboshDeviceStatus?.kiboshDevice?.currentFirmwareVersion ? "main-dark" : "red"}>
                        { currentKiboshDeviceStatus?.latestFirmwareVersion === currentKiboshDeviceStatus?.kiboshDevice?.currentFirmwareVersion ? "Current" : "Click Here to Upgrade" }
                      </text>
                    </text>
                  }
                  {
                    kiboshDevice.kiboshDeviceTypeCode === "router" &&
                      <a href="/portal/filters?dev=lan" target="_blank" >
                        <text data-wrap="wrap" data-weight="600">
                          Active Global Filters :
                          <text data-weight="700" data-wrap="wrap" data-color="main-dark">
                            { globalBlockedSites ? "" + ( globalBlockedSites.length) : "0" }
                          </text>
                        </text>
                      </a>

                  }

                  { kiboshDevice.kiboshDeviceTypeCode === "router" &&
                    <a href="/portal/dash?tab=data" >
                      <text data-wrap="wrap" data-weight="600" data-cursor="pointer">
                        Time:
                        <text data-weight="700" data-wrap="wrap" data-color="main-dark">
                          { currentTime }
                        </text>
                      </text>
                    </a>
                  }
                  
                  { isMobile && (isIOS || showHomescreenInstallButton)  && 
                    <group data-gap="10" data-direction="column" data-length="160" >
                      <button type="button" effect="material" className="button primary large" onClick={handleInstallClick} >
                        Create Mobile App
                      </button>
                    </group>
                  }
                </group>
              </group>
              <group data-gap="10" data-space-vertical="15" data-type="grid" data-grid-template="160">
                <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" data-cursor={ kiboshDevice.kiboshDeviceTypeCode === "router" ? "pointer" : "default" } onClick={ () => {if(kiboshDevice.kiboshDeviceTypeCode === "router"){ setShowLatencyModal(true)  }} } >
                  <group data-length="70" data-height="70" data-radius="50">
                    <icon data-text-size="xx-large" data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="500" data-color="main" data-elevation="5" data-background="main-color">language</icon>
                    <status data-position="absolute" data-length="15" data-height="15" data-radius="50" data-justify="center" data-background={currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs && currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs > 0 ? "main" : "red"} data-border="" data-top="0" data-right="0"></status>
                  </group>
                  <space data-height="10"></space>
                  <text data-weight="600">{ "Internet \nSpeed" }</text>
                  <text>{currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs && currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs > 0 ? `${currentKiboshDeviceStatus?.kiboshDevice?.wanPingMs.toFixed(2)} ms` : "Not Connected"}</text>
                  <text data-weight="600" data-color={getInternetLatencyClassColor()}>{getInternetLatencyClass()}</text>
                </group>
                {
                  kiboshDevice.kiboshDeviceTypeCode === "router" &&
                  <>
                    <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" data-cursor="pointer" onClick={() => onSelectedTab("devices")}>
                      <group data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="600" data-elevation="5" data-background="main-color">
                        <text data-text-size="large">{connectedDevicesCount}</text>
                        <status data-position="absolute" data-length="15" data-height="15" data-radius="50" data-justify="center" data-background="main" data-border="" data-top="0" data-right="0"></status>
                      </group>
                      <space data-height="10"></space>
                      <text data-max-length="100" data-wrap="wrap" data-text-align="center" style={{ whiteSpace:"pre-line" }} >{ "Connected \nDevices" }</text>
                    </group>
                    <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" data-cursor="pointer" onClick={() => onSelectedTab("vpns")}>
                      <group data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="600" data-elevation="5" data-background="main-color">
                        <text data-text-size="large">{vpnsCount}</text>
                        <status data-position="absolute" data-length="15" data-height="15" data-radius="50" data-justify="center" data-background="main" data-border="" data-top="0" data-right="0"></status>
                      </group>
                      <space data-height="10"></space>
                      <text data-max-length="100" data-wrap="wrap" data-text-align="center" style={{ whiteSpace:"pre-line" }} >{ "Manage \nMobile" }</text>

                    </group>
                  </>
                }
                <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" data-cursor={ malwaresCount ? "pointer" : ""} onClick={onMalwareClick}>
                  <group data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="600" data-elevation="5" data-background="main-color">
                    <text data-text-size="large">{malwaresCount}</text>
                    <status data-position="absolute" data-length="15" data-height="15" data-radius="50" data-justify="center" data-background={!!malwaresCount ? "red" : "main"} data-border="" data-top="0" data-right="0"></status>
                  </group>
                  <space data-height="10"></space>
                  <text data-max-length="100" data-wrap="wrap" data-text-align="center" >Malware Blocked</text>
                  {!!malwareLastUpdate &&
                  <>
                    <text data-max-length="160" data-wrap="wrap" data-text-align="center" data-color="main">{malwareLastUpdate}</text>
                  </>
                  }
                </group>


                <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" data-cursor={ explicitCount ? "pointer" : ""} onClick={onExplicitClick}>
                  <group data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="600" data-elevation="5" data-background="main-color">
                    <text data-text-size="large">{explicitCount}</text>
                    <status data-position="absolute" data-length="15" data-height="15" data-radius="50" data-justify="center" data-background={!!explicitCount ? "red" : "main"} data-border="" data-top="0" data-right="0"></status>
                  </group>
                  <space data-height="10"></space>
                  <text data-max-length="100" data-wrap="wrap" data-text-align="center" >Explicit Blocked</text>
                  {!!explicitLastUpdate &&
                  <>
                    <text data-max-length="160" data-wrap="wrap" data-text-align="center" data-color="main">{explicitLastUpdate}</text>
                  </>
                  }
                </group>

                <a href={ kiboshDevice.kiboshDeviceTypeCode === "router" ? "/portal/dash?tab=docs" : "https://kibosh.com/docs-category/home" } target="_blank" >
                  <group data-space-horizontal="20" data-space-vertical="5" data-direction="column" data-align="center" data-weight="600" >
                    <group data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="600" data-elevation="5" data-background="main-color">
                      <icon data-text-size="xx-large" data-length="70" data-height="70" data-radius="50" data-justify="center" data-align="center" data-weight="500" data-color="main" data-elevation="5" data-background="main-color">help</icon>
                    </group>
                    <space data-height="10"></space>
                    <text data-max-length="100" data-wrap="wrap" data-text-align="center" >Help</text>
                  </group>
                </a>


              </group>
              {
                kiboshDevice.kiboshDeviceTypeCode === "dns" && (!(currentKiboshDeviceStatus?.kiboshDevice?.currentExternalIp)) &&
                  <group>
                    <a href="https://kibosh.com/kl-start/" target="_blank" style={{ display: "block", height: "40px" }}>
	              <img src={ HandPointingRightSvg  } width="32" height="32" />
                      <text data-weight="700" data-text-size="x-large" data-wrap="wrap" data-color="orangered" style={{ color: "#ff4500", position: "absolute", top: "0px", left: "40px"  }} >Start Here</text>
                    </a>
                  </group>
              }
              {
                kiboshDevice.kiboshDeviceTypeCode === "dns" && (currentKiboshDeviceStatus?.kiboshDevice?.currentExternalIp) && currentKiboshDeviceStatus?.kiboshDevice?.connectionStatus === "disconnected" &&
                  <group>
                    <group>
                      <text data-weight="400" data-text-size="x-large" data-wrap="wrap" >KiboshDNS Status: &nbsp;&nbsp;<text data-weight="700" data-secondary data-text-size="x-large">Unknown</text></text>
                    </group>
                    <group>
                      <a href="https://kibosh.com/docs/kiboshdns-is-unknown" target="_blank" style={{ display: "block", height: "40px", marginTop: "12px" }}>
	                <img src={ HandPointingRightSvg  } width="32" height="32" />
                        <text data-weight="700" data-text-size="x-large" data-wrap="wrap" data-color="orangered" style={{ color: "#ff4500", position: "absolute", top: "12px", left: "40px" }} >Go here to fix...</text>
                      </a>
                    </group>
                  </group>
              }
              {
                kiboshDevice.kiboshDeviceTypeCode === "router" &&
                <group>
                  <separator data-horizontal=""></separator>
                  <group data-cursor="pointer" onClick={() => onSelectedTab("details")}>
                    <group data-type="column" data-column-size="160" data-wrap="no">
                      <group data-space="20" data-align="center">
                        <group data-direction="column" data-length='160'>
                          <text data-text-size="x-large" data-text-align="center" data-light="">{(downloadSpeed).toFixed(2)}</text>
                          <group>
                            <icon data-icon-size="48" data-color="main-dark">arrow_downward</icon>
                            <group data-direction="column" data-fit="1" data-justify="center">
                              <text data-weight="600" data-color="main-dark">Download</text>
                              <text data-light="">{downloadSpeedUnit}</text>
                            </group>
                          </group>
                        </group>
                      </group>
                      <group data-space="20" data-align="center">
                        <group data-direction="column" data-length='160'>
                          <text data-text-size="x-large" data-text-align="center" data-light="">{(uploadSpeed).toFixed(2)}</text>
                          <group>
                            <icon data-icon-size="48" data-color="main-dark">arrow_upward</icon>
                            <group data-direction="column" data-fit="1" data-justify="center">
                              <text data-weight="600" data-color="main-dark">Upload</text>
                              <text data-light="">{uploadSpeedUnit}</text>
                            </group>
                          </group>
                        </group>
                      </group>
                    </group>
                    <group data-justify={isMobile ? "center" : ""}>
                      <text data-weight="600" data-text-align="center" data-color="blue">Total Internet Used last 15 minutes</text>
                    </group>
                  </group>
                </group>
              }
            </group>
          </section>
        </view>
      </view>
      {!!showAccountKiboshDeviceModal && (
        <AccountKiboshDeviceModal
          addTabNameOnHeader={true}
          modalTitle={
              `${
                !kiboshDevice.accountKiboshDeviceId
                  ? "New Kibosh Device"
                  : `Kibosh Device Name: ${kiboshDevice.kiboshDeviceName}`
              }`
          }
          open={showAccountKiboshDeviceModal}
          showTabs={false}
          closeOnSave={true}
          account={currentAccount!}
          accountKiboshDevice={kiboshDevice}
          malwareDevicesOnly={malwareDevicesOnly}
          explicitDevicesOnly={explicitDevicesOnly}
          preSelectedTab={preSelectedTab}
          size="fullscreen"
          onClose={() => setShowAccountKiboshDeviceModal(false)}
        />
      )}

      {!!showLatencyModal && 
        <Popup 
          adaptive
          title={ "Latency" }
          onCancel={() => setShowLatencyModal(false)}
          onClose={() => setShowLatencyModal(false)}
          noCommandbar={true}
        >
          <AccountLatencyLogs account={currentAccount} kiboshDeviceReference={kiboshDevice.kiboshDeviceReference} onCancel={ () => { return true ; } } onSave={(acc) => { return(true); } } />

        </Popup>
      }
      {!!showExplicitModal && 
        <Popup 
          adaptive
          title={ "Explicit Logs" }
          onCancel={() => setShowExplicitModal(false)}
          onClose={() => setShowExplicitModal(false)}
          noCommandbar={true}
        >
          <AccountDeviceExplicitLogs account={currentAccount} kiboshDeviceReference={kiboshDevice.kiboshDeviceReference} onCancel={ () => { return true ; } } onSave={(acc) => { return(true); } }  />
        </Popup>
      }
      {!!showMalwareModal && 
        <Popup 
          adaptive
          title={ "Explicit Logs" }
          onCancel={() => setShowMalwareModal(false)}
          onClose={() => setShowMalwareModal(false)}
          noCommandbar={true}
        >
          <AccountDeviceMalwareLogs account={currentAccount} kiboshDeviceReference={kiboshDevice.kiboshDeviceReference} onCancel={ () => { return true ; } } onSave={(acc) => { return(true); } }  />
        </Popup>
      }
      <InfoPopup
        title=" "
        message={ [ 
		"Install App to iOS Homescreen:", <br/>, <br/>, 
		"1. Click the icon ", 
			<svg version="1.1" viewBox="0 0 2048 2048" width="24" height="24" xmlns="http://www.w3.org/2000/svg" >
				<path transform="translate(600,655)" d="m0 0h301l1 2v79l-1 1-284 1-17 2-10 5-6 5-6 10-3 11v751l3 12 7 10 8 6 8 3 12 2h776l54-1 12-4 9-7 6-10 3-14v-744l-2-12-5-10-5-6-14-7-4-1-18-1-278-1-1-1v-81h301l18 3 16 5 16 8 12 8 12 10v2l3 1 9 11 9 14 8 17 4 14 2 12 1 15v744l-2 21-6 21-9 19-10 14-12 13-10 8-11 7-14 7-18 6-17 3-13 1h-824l-20-2-19-5-16-7-12-7-16-13-11-12-10-15-8-16-5-17-3-18-1-42v-658l1-61 3-18 5-16 4-9 7-13 8-11 3-4h2l2-4 13-12 16-10 15-7 18-5z" />
				<path transform="translate(1023,229)" d="m0 0 5 3 269 269 1 4-53 53-2 3-4-1-172-172-1 3v753l-2 3h-81l-1-2v-758l-174 174h-3l-7-8-48-48 1-5 10-9 162-162 5-6 7-6 5-6 7-6 5-6 7-6 5-6 7-6 5-6 7-6 5-6 7-6 5-6 8-7z" />
			</svg>,
			" in the browser address bar", <br/>, 
		"2. Click 'Add to Home Screen'"
        ]  }
        okCallback={ () => { setIosInstructionPopupVisible(false); } }
        visible={ iosInstructionPopupVisible }
      />

    </>
  );
}
